import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Contestacao.css';
import { useAuth } from '../context/ApiKeyContext';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import CustomizationSliders from '../components/CustomizationSliders';
import {
  CONTESTATION_SERVICE,
  readServiceCustomization
} from '../utils/serviceCustomizationApi';

// URL base da API - usa variável de ambiente ou fallback para produção
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Contestacao = () => {
  const [progressValue, setProgressValue] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [temporaryError, setTemporaryError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [contestacaoHtml, setContestacaoHtml] = useState('');
  const [shouldStopPolling, setShouldStopPolling] = useState(false);
  
  // Refs para controlar o polling e detectar mudanças de página
  const pollingIntervalRef = useRef(null);
  const isPollingActiveRef = useRef(false);
  const pageVisibleRef = useRef(true);
  
  // Obter a API Key do contexto
  const { token, cell, user } = useAuth();
  const history = useHistory();
  const { t } = useTranslation('jobs');
  
  const [fileData, setFileData] = useState({
    inicialFile: null,
    subsidiosFiles: []
  });
  
  const [formData, setFormData] = useState({
    lawyerName: '',
    lawyerOAB: '',
    local: '',
    date: '',
    empresaDefendida: ''
  });

  // Estados para parâmetros de personalização
  const [useDefaultParams, setUseDefaultParams] = useState(true);
  const [customizationParams, setCustomizationParams] = useState({
    creativity: 2,
    tone: 2,
    scope_topics: 2,
    complexity: 2,
    foundation_depth: 2,
  });
  const [observations, setObservations] = useState({
    client: '',
    internal: '',
  });

  // Garantir que observations sempre tenha valores válidos
  const safeObservations = {
    client: observations?.client || '',
    internal: observations?.internal || '',
  };

  // Função para verificar se o usuário tem permissão para editar parâmetros na criação
  const hasCustomizationPermission = () => {
    return user && user.permissions && (
      user.permissions.includes('CONTEST_PARAMS_EDIT_ON_RUN') ||
      user.permissions.includes('CONTEST_PARAMS') ||
      user.permissions.includes('CONTEST_PARAMS_SUPERUSER')
    );
  };

  // Handlers estáveis para as observações usando useCallback
  const handleClientObservationChange = useCallback((e) => {
    try {
      console.log('handleClientObservationChange called', { e, target: e?.target });
      
      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn('Event target is null in handleClientObservationChange', { e });
        return;
      }
      
      const value = e.target.value;
      console.log('Setting client observation value:', value);
      
      setObservations(prev => {
        console.log('Previous observations:', prev);
        const newObs = { ...prev, client: value };
        console.log('New observations:', newObs);
        return newObs;
      });
    } catch (error) {
      console.error('Erro em handleClientObservationChange:', error, { 
        event: e, 
        stack: error.stack 
      });
    }
  }, []);

  const handleInternalObservationChange = useCallback((e) => {
    try {
      console.log('handleInternalObservationChange called', { e, target: e?.target });
      
      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn('Event target is null in handleInternalObservationChange', { e });
        return;
      }
      
      const value = e.target.value;
      console.log('Setting internal observation value:', value);
      
      setObservations(prev => {
        console.log('Previous observations:', prev);
        const newObs = { ...prev, internal: value };
        console.log('New observations:', newObs);
        return newObs;
      });
    } catch (error) {
      console.error('Erro em handleInternalObservationChange:', error, { 
        event: e, 
        stack: error.stack 
      });
    }
  }, []);

  const handleUseDefaultParamsChange = useCallback((e) => {
    try {
      console.log('handleUseDefaultParamsChange called', { e, target: e?.target });
      
      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn('Event target is null in handleUseDefaultParamsChange', { e });
        return;
      }
      
      const checked = e.target.checked;
      console.log('Setting useDefaultParams:', checked);
      
      setUseDefaultParams(checked);
    } catch (error) {
      console.error('Erro em handleUseDefaultParamsChange:', error, { 
        event: e, 
        stack: error.stack 
      });
    }
  }, []);

  // Função para carregar valores padrão da célula
  const loadDefaultValues = async () => {
    if (!cell?.value || !token || !hasCustomizationPermission()) return;

    try {
      // Carregar parâmetros de personalização padrão
      const profileParams = await readServiceCustomization(
        token,
        cell.value,
        CONTESTATION_SERVICE.ATTRIBUTES.PROFILE_PARAMS
      );
      
      if (profileParams) {
        setCustomizationParams(prev => ({ ...prev, ...profileParams }));
      }

      // Carregar regras padrão do cliente
      const clientRules = await readServiceCustomization(
        token,
        cell.value,
        CONTESTATION_SERVICE.ATTRIBUTES.RULES
      );
      
      // Carregar regras internas padrão (apenas para superusuários)
      let internalRules = '';
      if (user?.permissions?.includes('CONTEST_PARAMS_SUPERUSER')) {
        internalRules = await readServiceCustomization(
          token,
          cell.value,
          CONTESTATION_SERVICE.ATTRIBUTES.RULES_INTERNAL
        );
      }
      
      if (clientRules || internalRules) {
        setObservations({
          client: clientRules || '',
          internal: internalRules || '',
        });
      }
    } catch (error) {
      console.error('Erro ao carregar valores padrão:', error);
    }
  };
  
  useEffect(() => {
    validateForm();
  }, [fileData, formData]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Carregar valores padrão quando o componente for montado
  useEffect(() => {
    loadDefaultValues();
  }, [cell?.value, token, user]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Função para limpar o polling
  const cleanupPolling = useCallback(() => {
    console.log('🛑 Limpando polling...');
    setShouldStopPolling(true);
    isPollingActiveRef.current = false;
    
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Controlar visibilidade da página usando Page Visibility API
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      pageVisibleRef.current = isVisible;
      
      console.log(`📄 Página ${isVisible ? 'visível' : 'oculta'}`);
      
      // Se a página ficou oculta e há polling ativo, pausar
      if (!isVisible && isPollingActiveRef.current) {
        console.log('⏸️ Pausando polling - página não está visível');
        cleanupPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cleanupPolling]);

  // Cleanup para parar o polling quando o componente for desmontado
  useEffect(() => {
    return () => {
      console.log('🧹 Componente desmontado - limpando polling');
      cleanupPolling();
    };
  }, [cleanupPolling]);
  
  const handleFileChange = (e) => {
    // Verificação de segurança para evitar erro quando e.target é null
    if (!e || !e.target) {
      console.warn('Event target is null in handleFileChange');
      return;
    }
    
    const { id, files } = e.target;
    
    if (id === 'subsidios-files') {
      setFileData(prev => ({
        ...prev,
        subsidiosFiles: Array.from(files)
      }));
      
      // Atualizar exibição de arquivos selecionados
      const filesContainer = document.getElementById('subsidios-list');
      filesContainer.innerHTML = '';
      
      if (files.length > 0) {
        Array.from(files).forEach(file => {
          const fileChip = document.createElement('div');
          fileChip.className = 'file-chip';
          fileChip.innerHTML = `
            ${file.name}
            <span class="remove">×</span>
          `;
          filesContainer.appendChild(fileChip);
          
          // Simulação da remoção do arquivo
          const removeButton = fileChip.querySelector('.remove');
          if (removeButton) {
            removeButton.addEventListener('click', function(e) {
              // Verificação de segurança para evitar erros
              if (!e) {
                console.warn('Event is null in file remove click handler');
                return;
              }
              
              try {
                fileChip.remove();
                const remaining = Array.from(document.querySelectorAll('.file-chip')).map(chip => 
                  chip.textContent.replace('×', '').trim()
                );
                
                setFileData(prev => ({
                  ...prev,
                  subsidiosFiles: prev.subsidiosFiles.filter(f => remaining.includes(f.name))
                }));
              } catch (error) {
                console.error('Erro ao remover arquivo:', error);
              }
            });
          }
        });
      }
    } else if (id === 'inicial-file') {
      setFileData(prev => ({
        ...prev,
        inicialFile: files.length > 0 ? files[0] : null
      }));
      
      console.log(`Arquivo ${id} selecionado:`, files[0]?.name);
    }
  };
  
  const handleInputChange = (e) => {
    // Verificação de segurança para evitar erro quando e.target é null
    if (!e || !e.target) {
      console.warn('Event target is null in handleInputChange');
      return;
    }
    
    const { id, value } = e.target;
    
    // Para sliders de personalização, converter para número
    const isPersonalizationSlider = ['criativity', 'tone', 'scope_topics', 'complexity', 'foundation_depth'].includes(id);
    const finalValue = isPersonalizationSlider ? parseInt(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [id]: finalValue
    }));
  };
  
  const validateForm = () => {
    const { inicialFile } = fileData;
    const { lawyerName, lawyerOAB, local, date, empresaDefendida } = formData;
    
    const isValid = 
      inicialFile !== null && 
      lawyerName.trim() !== '' && 
      lawyerOAB.trim() !== '' && 
      local.trim() !== '' && 
      date !== '' &&
      empresaDefendida.trim() !== '';
    
    setFormValid(isValid);
  };
  
  const handleProcessClick = async () => {
    // Validar os dados novamente antes de processar
    if (!formValid) return;
    
    // Verificar se o token está presente
    if (!token) {
      setError(t('common:authTokenNotFound'));
      return;
    }
    
    // Se já existe um resultado sendo mostrado, reiniciar o processo
    if (showResult || progressValue >= 100) {
      // Limpar os estados para permitir novo processamento
      setShowResult(false);
      setCurrentStep(0);
      setProgressValue(0);
      setJobId(null);
      setContestacaoHtml('');
      setError(null);
      
      // Ocultar as barras de progresso
      document.getElementById('status-steps').style.display = 'none';
      document.getElementById('progress-container').style.display = 'none';
      
      // Pequeno delay para garantir que a interface seja atualizada
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Limpar qualquer polling anterior antes de iniciar um novo
    cleanupPolling();
    
    setError(null);
    setIsProcessing(true);
    setShowResult(false);
    setShouldStopPolling(false); // Reset flag para permitir novo polling
    
    // Inicialmente, não mostrar as barras de progresso ainda
    // Elas serão mostradas apenas após receber o job_id com sucesso
    
    try {
      // Preparar os dados para a API usando FormData
      const apiFormData = new FormData();
      
      // Adicionar os arquivos
      apiFormData.append('peticao_inicial', fileData.inicialFile);
      
      // Adicionar os subsídios (podem ser múltiplos arquivos)
      fileData.subsidiosFiles.forEach(file => {
        apiFormData.append('subsidios', file);
      });
      
      // Arquivos opcionais removidos do envio
      
      // Adicionar os dados do advogado (usar o estado formData)
      apiFormData.append('nome_advogado', formData.lawyerName);
      apiFormData.append('oab_advogado', formData.lawyerOAB);
      apiFormData.append('local', formData.local);
      apiFormData.append('data_documento', formData.date);
      apiFormData.append('empresa_defendida', formData.empresaDefendida);
      
      // Adicionar parâmetros de personalização se não estiver usando padrões da célula
      if (!useDefaultParams) {
        // Criar objeto CustomProfile com os parâmetros de personalização
        const customProfile = {
          creativity: customizationParams.creativity,
          tone: customizationParams.tone,
          scope_topics: customizationParams.scope_topics,
          complexity: customizationParams.complexity,
          foundation_depth: customizationParams.foundation_depth
        };
        
        // Adicionar como JSON string
        apiFormData.append('custom_profile', JSON.stringify(customProfile));
        
        // Adicionar regras sempre (mesmo se vazias)
        apiFormData.append('regras', safeObservations.client);
        
        if (user?.permissions?.includes('CONTEST_PARAMS_SUPERUSER')) {
          apiFormData.append('regras_internas', safeObservations.internal);
        }
      }
      
      // URL da API
      const cellId = cell?.value || 'undefined';
      const url = `${API_BASE_URL}/doc_creator/cria_contestacao?cell_id=${cellId}`;
      
      // Log para diagnóstico
      console.log(`Cell value na criação:`, cell?.value);
      console.log(`Enviando solicitação para: ${url}`);
      
      // Verificar o conteúdo do formData antes de enviar (para diagnóstico)
      for (let pair of apiFormData.entries()) {
        console.log(`FormData - ${pair[0]}: `, pair[1]);
      }
      
      // Fazer a chamada para iniciar o processamento
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
          // Não definimos o Content-Type explicitamente pois o browser
          // o ajustará automaticamente com o boundary correto para FormData
        },
        body: apiFormData
      });
      
      if (!response.ok) {
        // Tentar obter mais detalhes do erro
        try {
          const errorData = await response.json();
          throw new Error(`Erro na requisição: ${response.status} ${response.statusText}. Detalhes: ${JSON.stringify(errorData)}`);
        } catch (parseError) {
          throw new Error(`Erro na requisição: ${response.status} ${response.statusText}`);
        }
      }
      
      const data = await response.json();
      
      if (!data.job_id) {
        throw new Error(t('common:invalidResponse'));
      }
      
      const receivedJobId = data.job_id;
      console.log(`Job iniciado com sucesso! Job ID: ${receivedJobId}`);
      setJobId(receivedJobId);
      
      // Só agora que temos o job_id, mostramos as barras de progresso
      // e atualizamos os estados relacionados ao progresso
      setCurrentStep(1);
      setProgressValue(0);
      
      // Mostrar passos e barra de progresso
      document.getElementById('status-steps').style.display = 'block';
      document.getElementById('progress-container').style.display = 'block';
      
      // Iniciar polling para verificar o status do job
      console.log(`Iniciando polling para verificar o status do job ${receivedJobId}...`);
      await checkJobStatus(receivedJobId);
      
    } catch (error) {
      console.error('Erro ao processar documento:', error);
      setError(`${t('common:processingError')} ${error.message}`);
      setIsProcessing(false);
    }
  };
  
  // Função para buscar o conteúdo HTML através do html_preview_token
  // Usando a mesma metodologia do componente FileDownload
  const fetchHtmlContent = async (htmlPreviewToken) => {
    console.log('🎬 fetchHtmlContent iniciada com token:', htmlPreviewToken);
    
    if (!htmlPreviewToken || !token) {
      console.warn('❌ Token HTML preview ou token de autenticação não disponível:', { htmlPreviewToken, hasToken: !!token });
      setContestacaoHtml(`<div style="text-align: center; padding: 50px;"><h2>${t('contestacaoCreate.previewNotAvailable')}</h2><p>${t('contestacaoCreate.previewNotAvailableDesc')}</p></div>`);
      return;
    }
    
    try {
      console.log('📡 Buscando conteúdo HTML do documento...');
      
      const previewUrl = `${API_BASE_URL}/files/download?token=${htmlPreviewToken}`;
      console.log('🌐 URL de preview:', previewUrl);
      
      const response = await fetch(previewUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'text/html',
        },
      });
      
      console.log('📨 Resposta do preview:', { status: response.status, ok: response.ok });
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar conteúdo HTML: ${response.status} - ${response.statusText}`);
      }
      
      const htmlContent = await response.text();
      console.log('✅ Conteúdo HTML carregado com sucesso - tamanho:', htmlContent.length);
      setContestacaoHtml(htmlContent);
      console.log('✅ HTML injetado na interface');
      
    } catch (error) {
      console.error('❌ Erro ao buscar conteúdo HTML:', error);
      setContestacaoHtml(`<div style="text-align: center; padding: 50px;"><h2>${t('contestacaoCreate.errorLoadingPreview')}</h2><p>${t('contestacaoCreate.errorLoadingPreviewDesc')}</p></div>`);
    }
  };
  
  // Função para buscar as informações completas do job e depois o conteúdo HTML
  const fetchJobDetailsAndHtml = async (jobId) => {
    console.log('🚀 fetchJobDetailsAndHtml chamada com:', { jobId, hasToken: !!token });
    
    if (!jobId || !token) {
      console.warn('❌ Job ID ou token de autenticação não disponível:', { jobId, hasToken: !!token });
      return;
    }
    
    try {
      console.log('📡 Buscando informações completas do job:', jobId);
      
      // Buscar as informações completas do job usando o endpoint correto
      const resultUrl = `${API_BASE_URL}/job/job_result?job_id=${jobId}`;
      
      console.log('🌐 Fazendo chamada para:', resultUrl);
      
      const statusResponse = await fetch(resultUrl, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: ''
      });
      
      console.log('📨 Resposta recebida:', { status: statusResponse.status, ok: statusResponse.ok });
      
      if (!statusResponse.ok) {
        throw new Error(`Erro ao obter informações completas do job: ${statusResponse.status} ${statusResponse.statusText}`);
      }
      
      const statusData = await statusResponse.json();
      console.log('✅ Informações completas do job recebidas:', statusData);
      console.log('📄 Resultado completo:', statusData.result);
      
      // Verificar se há o html_preview_token na estrutura correta
      console.log('🔍 Verificando html_preview_token na estrutura:', {
        hasResult: !!statusData.result,
        hasResultDocx: !!(statusData.result && statusData.result.result_docx),
        hasHtmlPreviewToken: !!(statusData.result && statusData.result.result_docx && statusData.result.result_docx.html_preview_token),
        htmlPreviewToken: statusData.result?.result_docx?.html_preview_token
      });
      
      if (statusData.result && statusData.result.result_docx && statusData.result.result_docx.html_preview_token) {
        console.log('✅ HTML preview token encontrado:', statusData.result.result_docx.html_preview_token);
        console.log('🎯 Chamando fetchHtmlContent...');
        await fetchHtmlContent(statusData.result.result_docx.html_preview_token);
        console.log('✅ fetchHtmlContent concluída');
      } else {
        console.warn('❌ HTML preview token não encontrado no result');
        console.warn('📊 Estrutura disponível:', {
          hasResult: !!statusData.result,
          hasResultDocx: !!(statusData.result && statusData.result.result_docx),
          hasHtmlPreviewToken: !!(statusData.result && statusData.result.result_docx && statusData.result.result_docx.html_preview_token)
        });
        setContestacaoHtml(`<div style="text-align: center; padding: 50px;"><h2>${t('contestacaoCreate.previewNotAvailableSuccess')}</h2><p>${t('contestacaoCreate.previewNotAvailableSuccessDesc')}</p></div>`);
      }
      
    } catch (error) {
      console.error('❌ Erro ao buscar informações do job:', error);
      setContestacaoHtml(`<div style="text-align: center; padding: 50px;"><h2>${t('contestacaoCreate.errorLoadingInfo')}</h2><p>${t('contestacaoCreate.errorLoadingInfoDesc')}</p></div>`);
    }
  };
  
  // Função para verificar o status do job
  const checkJobStatus = async (jobId) => {
    // Verificar se já existe polling ativo para evitar duplicação
    if (isPollingActiveRef.current) {
      console.log('⚠️ Polling já está ativo, ignorando nova solicitação');
      return;
    }
    
    // Verificar se a página está visível antes de iniciar
    if (!pageVisibleRef.current) {
      console.log('⚠️ Página não está visível, não iniciando polling');
      return;
    }
    
    isPollingActiveRef.current = true;
    let isDone = false;
    let attempts = 0;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 3; // Máximo de 3 erros consecutivos
    const MAX_TOTAL_ATTEMPTS = 60; // Máximo de 60 tentativas (5 minutos com intervalo de 5s)
    
    console.log('🔄 Iniciando polling para job:', jobId);
    
    while (!isDone && !shouldStopPolling && pageVisibleRef.current && attempts < MAX_TOTAL_ATTEMPTS) {
      try {
        // Verificar limites antes de continuar
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          console.log(`❌ Muitos erros consecutivos (${consecutiveErrors}), parando polling`);
          setError(`${t('common:tooManyErrors')} Tentativas interrompidas após ${consecutiveErrors} erros consecutivos.`);
          break;
        }

        // Esperar entre as verificações
        if (attempts > 0) {
          // Backoff exponencial após erros consecutivos
          const baseWaitTime = 5000; // 5 segundos base
          const backoffMultiplier = Math.min(Math.pow(1.5, consecutiveErrors), 4); // Máximo 4x o tempo base
          const waitTime = Math.floor(baseWaitTime * backoffMultiplier);
          
          console.log(`Aguardando ${waitTime/1000} segundos antes da próxima verificação... (tentativa ${attempts + 1}/${MAX_TOTAL_ATTEMPTS}, erros consecutivos: ${consecutiveErrors})`);
          
          // Usar setTimeout com ref para poder cancelar se necessário
          await new Promise((resolve) => {
            pollingIntervalRef.current = setTimeout(resolve, waitTime);
          });
          
          // Verificar se deve parar o polling após o wait
          if (shouldStopPolling || !pageVisibleRef.current) {
            console.log('🛑 Polling interrompido - página não visível ou solicitação de parada');
            isPollingActiveRef.current = false;
            return;
          }
        }
        
        attempts++;
        
        // URL para verificar o status (polling)
        const cellId = cell?.value || 'undefined';
        const url = `${API_BASE_URL}/doc_creator/status/${jobId}?cell_id=${cellId}`;
        
        // Log para diagnóstico
        console.log(`Verificando status do job ${jobId} (tentativa ${attempts})...`);
        console.log(`Cell object:`, cell);
        console.log(`Cell value:`, cell?.value);
        console.log(`URL: ${url}`);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Tratamento especial para códigos de erro específicos que indicam falha permanente
        if (response.status === 404) {
          throw new Error(t('common:jobNotFound'));
        }

        if (response.status === 401 || response.status === 403) {
          throw new Error(t('common:authError', { status: response.status }));
        }
        
        if (!response.ok) {
          consecutiveErrors++;
          console.log(`⚠️ Erro na requisição (${response.status}), erros consecutivos: ${consecutiveErrors}`);
          setTemporaryError(`${t('common:statusCheckError')} (${response.status} ${response.statusText}). ${t('common:tryingAgain')}`);
          setTimeout(() => setTemporaryError(null), 10000);
          continue;
        }
        
        const data = await response.json();
        console.log('Status recebido:', data);
        
        // Resetar contador de erros consecutivos em caso de sucesso
        consecutiveErrors = 0;
        
        // Verificar se o trabalho está concluído
        console.log('Verificando condições de conclusão:', {
          status: data.status,
          percentage: data.percentage,
          percentageType: typeof data.percentage,
          isDone: data.status === 'DONE',
          isComplete: data.percentage && (data.percentage >= 1.0 || (data.percentage <= 1.0 && data.percentage >= 0.99))
        });
        
        // Verifica se está concluído - aceita tanto percentage decimal (1.0) quanto percentual (100)
        const isComplete = data.percentage && (data.percentage >= 1.0 || (data.percentage <= 1.0 && data.percentage >= 0.99));
        
        if (data.status === 'DONE' && isComplete) {
          console.log('✅ Status DONE detectado - parando polling');
          isDone = true;
          isPollingActiveRef.current = false;
          setShouldStopPolling(true); // Garantir que o flag também seja definido
          
          console.log('✅ Condições atendidas - iniciando busca dos dados completos do job');
          setCurrentStep(5);
          setProgressValue(100);
          setShowResult(true);
          console.log('Processamento concluído com sucesso!');
          
          // Debug: verificar o que está vindo no data
          console.log('Dados recebidos no status DONE:', data);
          console.log('Output payload no status:', data.output_payload);
          
          // Nova metodologia: buscar as informações completas do job para obter o output_payload
          console.log('🔍 Buscando informações completas do job para obter o conteúdo HTML...');
          await fetchJobDetailsAndHtml(jobId);
          
          setIsProcessing(false);
          return;
          
        } else if (data.status === 'ERROR') {
          consecutiveErrors++;
          const errorMessage = data.error || t('common:error');
          console.log(`⚠️ Status ERROR recebido, erros consecutivos: ${consecutiveErrors}`);
          setTemporaryError(`${t('common:processingError')} ${errorMessage}. ${t('common:tryingAgain')}`);
          setTimeout(() => setTemporaryError(null), 10000);
          continue;
        } else if (data.status === 'RUNNING') {
          const step = data.current_step || 1;
          const totalSteps = data.total_steps || 5;
          const percentage = data.percentage ? Math.round(data.percentage * 100) : 0;
          
          setCurrentStep(step);
          setProgressValue(percentage);
          console.log(`Documento em processamento... Passo: ${step}/${totalSteps}, Progresso: ${percentage}%`);
        }
      } catch (error) {
        console.error('Erro ao verificar status do job:', error);
        consecutiveErrors++;
        
        // Apenas erros críticos que realmente impedem o funcionamento farão o processo parar
        if (
          error.message.includes("404") ||
          error.message.includes("401") ||
          error.message.includes("403") ||
          error.message.includes(t('common:processingError'))
        ) {
          console.log('❌ Erro crítico detectado - parando polling');
          isPollingActiveRef.current = false;
          setShouldStopPolling(true);
          setError(`${t('common:statusCheckFailed')} ${error.message}`);
          setIsProcessing(false);
          return;
        }
        
        // Para outros erros, mostrar mensagem temporária mas continuar tentando
        console.log(`⚠️ Erro capturado no catch, erros consecutivos: ${consecutiveErrors}`);
        setTemporaryError(`${t('common:statusCheckError')}: ${error.message}. ${t('common:tryingAgain')}`);
        setTimeout(() => setTemporaryError(null), 10000);
        continue;
      }
    }
    
    // Garantir que o polling seja marcado como inativo ao sair do loop
    console.log('🔄 Saindo do loop de polling');
    isPollingActiveRef.current = false;
    
    // Verificar se saiu do loop por atingir limite de tentativas
    if (attempts >= MAX_TOTAL_ATTEMPTS) {
      console.log(`⏰ Limite máximo de tentativas atingido (${MAX_TOTAL_ATTEMPTS})`);
      setError(`${t('common:maxAttemptsReached')} Processo interrompido após ${MAX_TOTAL_ATTEMPTS} tentativas.`);
      setIsProcessing(false);
    }
  };


  const handleDownloadDocument = async () => {
    if (!jobId) {
      setError(t('common:downloadError'));
      return;
    }
    
    try {
      // Primeiro, obter as informações do job para pegar o token do arquivo
      const resultUrl = `${API_BASE_URL}/job/job_result?job_id=${jobId}`;
      
      const statusResponse = await fetch(resultUrl, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: ''
      });
      
      if (!statusResponse.ok) {
        throw new Error(`Erro ao obter informações do job: ${statusResponse.status} ${statusResponse.statusText}`);
      }
      
      const statusData = await statusResponse.json();
      
      // Verificar se há o token do arquivo docx na estrutura correta
      if (!statusData.result || !statusData.result.result_docx || !statusData.result.result_docx.token) {
        throw new Error(t('common:documentTokenNotFound'));
      }
      
      const fileToken = statusData.result.result_docx.token;
      const filename = statusData.result.result_docx.filename || `Contestacao_${new Date().toISOString().substring(0, 10)}.docx`;
      
      // Fazer o download usando o token do arquivo
      const downloadUrl = `${API_BASE_URL}/files/download?token=${fileToken}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/octet-stream',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao baixar documento: ${response.status} ${response.statusText}`);
      }
      
      // Obter o blob do documento
      const blob = await response.blob();
      
      // Criar um link para download
      const downloadLink = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadLink;
      link.download = filename;
      
      // Adicionar o link ao documento, clicar nele e removê-lo
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Liberar a URL
      window.URL.revokeObjectURL(downloadLink);
      
    } catch (error) {
      console.error('Erro ao baixar o documento:', error);
      setError(`${t('common:downloadFailed')} ${error.message}`);
    }
  };
  


  return (
    <div className="container">
      <header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1>{t('contestacaoCreate.title')}</h1>
          </div>
          <button 
            onClick={() => history.push('/contestacoes')}
            style={{
              backgroundColor: 'var(--ikaro-orange-primary)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {t('contestacaoCreate.backToList')}
          </button>
        </div>
      </header>
      
      <div className="card">
        <h3 className="card-title">
          <i>📄</i> {t('contestacaoCreate.requiredFiles')}
        </h3>
        
        <div className="form-group">
          <div className="file-input">
            <label className="required">{t('contestacaoCreate.initialPetition')}</label>
            <div className="file-upload">
              <div className="file-upload-label">
                <i>📎</i>
                <span className="file-name">
                  {fileData.inicialFile ? fileData.inicialFile.name : t('contestacaoCreate.selectFile')}
                </span>
              </div>
              <input 
                type="file" 
                id="inicial-file" 
                accept=".pdf,.jpg,.jpeg,.png"
                required 
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="info-text">{t('contestacaoCreate.initialPetitionInfo')}</div>
        </div>
        
        <div className="form-group">
          <div className="file-input">
            <label>{t('contestacaoCreate.subsidies')}</label>
            <div className="file-upload">
              <div className="file-upload-label">
                <i>📎</i>
                <span className="file-name">
                  {fileData.subsidiosFiles.length > 0 
                    ? `${fileData.subsidiosFiles.length} ${t('contestacaoCreate.filesSelected')}` 
                    : t('contestacaoCreate.selectFiles')}
                </span>
              </div>
              <input 
                type="file" 
                id="subsidios-files" 
                multiple 
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="multiple-files" id="subsidios-list"></div>
          <div className="info-text">{t('contestacaoCreate.subsidiesInfo')}</div>
        </div>
        
      </div>
      
      <div className="card">
        <h3 className="card-title">
          <i>🏢</i> {t('contestacaoCreate.companyInfo')}
        </h3>
        
        <div className="form-group">
          <label htmlFor="empresaDefendida" className="required">{t('contestacaoCreate.defendedCompany')}</label>
          <input 
            type="text" 
            id="empresaDefendida" 
            placeholder={t('contestacaoCreate.defendedCompanyPlaceholder')} 
            required
            value={formData.empresaDefendida}
            onChange={handleInputChange}
          />
        </div>
      </div>
      
      <div className="card">
        <h3 className="card-title">
          <i>✏️</i> {t('contestacaoCreate.lawyerInfo')}
        </h3>
        
        <div className="form-group">
          <label htmlFor="lawyerName" className="required">{t('contestacaoCreate.fullName')}</label>
          <input 
            type="text" 
            id="lawyerName" 
            placeholder={t('contestacaoCreate.fullNamePlaceholder')} 
            required 
            value={formData.lawyerName}
            onChange={handleInputChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="lawyerOAB" className="required">{t('contestacaoCreate.oabNumber')}</label>
          <input 
            type="text" 
            id="lawyerOAB" 
            placeholder={t('contestacaoCreate.oabNumberPlaceholder')} 
            required
            value={formData.lawyerOAB}
            onChange={handleInputChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="local" className="required">{t('contestacaoCreate.location')}</label>
          <input
            type="text" 
            id="local" 
            placeholder={t('contestacaoCreate.locationPlaceholder')} 
            required
            value={formData.local}
            onChange={handleInputChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="date" className="required">{t('contestacaoCreate.contestationDate')}</label>
          <input 
            type="date" 
            id="date" 
            required
            value={formData.date}
            onChange={handleInputChange}
                    />
        </div>
        
      </div>
      
      {/* Seção de parâmetros - só aparece para usuários com permissão */}
      {hasCustomizationPermission() && (
        <div className="card">
          <h3 className="card-title">
            <i>⚙️</i> Parâmetros
          </h3>
          
          <div className="form-group">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={useDefaultParams}
                onChange={handleUseDefaultParamsChange}
              />
              <span className="checkmark"></span>
              Usar parâmetros padrões
            </label>
            <p className="info-text">
              Quando marcado, utiliza as configurações padrões definidas para esta célula. 
              Desmarque para personalizar os parâmetros desta contestação.
            </p>
          </div>
          
          {!useDefaultParams && (
            <div className="expanded-params">
              <div className="params-section">
                <h4 className="section-title">
                  <i>🎛️</i> Parâmetros de Personalização
                </h4>
                
                <CustomizationSliders
                  initialValues={customizationParams}
                  onChange={setCustomizationParams}
                />
              </div>

              <div className="custom-rules-section">
                <h4 className="section-title">
                  <i>📋</i> Regras Customizadas
                </h4>
                
                <div className="form-group">
                  <label htmlFor="client-custom-rules">Regras Customizadas do Cliente:</label>
                  <textarea
                    id="client-custom-rules"
                    placeholder="Regras específicas e diretrizes personalizadas para a construção da contestação..."
                    value={safeObservations.client}
                    onChange={handleClientObservationChange}
                    rows="4"
                  />
                </div>

                {user?.permissions?.includes('CONTEST_PARAMS_SUPERUSER') && (
                  <div className="form-group">
                    <label htmlFor="internal-custom-rules">Regras Internas:</label>
                    <textarea
                      id="internal-custom-rules"
                      placeholder="Regras e diretrizes para uso interno da equipe..."
                      value={safeObservations.internal}
                      onChange={handleInternalObservationChange}
                      rows="4"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <div className="error-alert">
          <i className="error-icon">⚠️</i>
          <span>{error}</span>
        </div>
      )}
      
      {temporaryError && (
        <div className="error-alert temporary">
          <i className="error-icon">⚠️</i>
          <span>{temporaryError}</span>
        </div>
      )}
      
      <button 
        id="process-btn" 
        className="process-btn"
        disabled={!formValid || isProcessing}
        onClick={handleProcessClick}
      >
        {isProcessing 
          ? t('contestacaoCreate.processing') 
          : progressValue >= 100 && showResult 
            ? t('contestacaoCreate.generateAgain') 
            : t('contestacaoCreate.generateContestation')
        }
      </button>
      
      <div className="status-steps" id="status-steps" style={{ display: currentStep > 0 ? 'block' : 'none' }}>
        {[1, 2, 3, 4, 5].map(step => (
          <div 
            key={step}
            className={`step ${currentStep === step ? 'active' : ''} ${currentStep > step || progressValue >= 100 ? 'completed' : ''}`} 
            data-step={step}
          >
            <div className="step-number">{step}</div>
            <div className="step-label">
              {step === 1 && t('contestacaoCreate.steps.1')}
              {step === 2 && t('contestacaoCreate.steps.2')}
              {step === 3 && t('contestacaoCreate.steps.3')}
              {step === 4 && t('contestacaoCreate.steps.4')}
              {step === 5 && t('contestacaoCreate.steps.5')}
            </div>
          </div>
        ))}
      </div>
      
      <div className="progress-container" id="progress-container" style={{ display: currentStep > 0 ? 'block' : 'none' }}>
        <div className="progress-label">
          <span id="progress-text">
            {currentStep === 1 && t('contestacaoCreate.steps.1')}
            {currentStep === 2 && t('contestacaoCreate.steps.2')}
            {currentStep === 3 && t('contestacaoCreate.steps.3')}
            {currentStep === 4 && t('contestacaoCreate.steps.4')}
            {currentStep === 5 && progressValue < 100 && t('contestacaoCreate.steps.5')}
            {progressValue >= 100 && t('contestacaoCreate.completed')}
          </span>
          <span id="progress-percentage">{progressValue}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" id="progress-fill" style={{ width: `${progressValue}%` }}></div>
        </div>
        
        {progressValue >= 100 && !isProcessing && (
          <div className="progress-complete">
            <p className="complete-message">{t('contestacaoCreate.processingCompleted')}</p>
            <p className="complete-instruction">{t('contestacaoCreate.processingInstruction')}</p>
          </div>
        )}
      </div>
      
      <div className="result-container" id="result-container" style={{ display: showResult ? 'block' : 'none' }}>
        <h3 className="card-title">
          <i>✅</i> {t('contestacaoCreate.contestationGenerated')}
        </h3>
        <div 
          className="document-preview" 
          id="document-preview"
          dangerouslySetInnerHTML={{ __html: contestacaoHtml || `<div style="text-align: center; padding: 50px;"><h2>${t('contestacaoCreate.waitingContent')}</h2><p>${t('contestacaoCreate.waitingContentDescription')}</p></div>` }}
        >
        </div>
        <div className="actions">
          <button 
            className="btn btn-primary" 
            id="download-btn"
            onClick={handleDownloadDocument}
          >
            {t('contestacaoCreate.downloadDocument')}
          </button>
        </div>
      </div>
      
      <footer>
        <p>{t('contestacaoCreate.footer')}</p>
      </footer>
    </div>
  );
};

export default Contestacao; 